import { useCallback, useEffect, useMemo } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { useAvailableTeleconsultoria } from "../../hooks/useAvailableTeleconsultoria";
import { useTeleconsultoriaPresence } from "../../presence/useTeleconsultoriaPresence";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import type { TeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { buildTeleconsultorChoices } from "../helpers/teleconsultorChoices";
import { listProviderAgendaSlots, type ProviderAgendaSlot } from "../services/providerAgendaService";

const EMPTY_SLOTS: ProviderAgendaSlot[] = [];

/**
 * Marcação da AFILIADA: teleconsultores da prestadora no período do filtro
 * (horários livres + disponíveis agora, numa lista só) e quem está online.
 * `refreshKey` muda depois de marcar/cancelar: o horário usado some/volta.
 */
export function useAffiliateBookingOptions(range: TeleconsultoriaDateRange, refreshKey: number) {
  const { profile } = useTeleconsultoriaProfile();
  const providerToken = profile.kind === "affiliate" ? profile.providerToken : "";
  const { startDate, endDate } = range;

  const slotsTask = useAsyncTask({
    task: useCallback(
      () => listProviderAgendaSlots(providerToken, { startDate, endDate }),
      [providerToken, startDate, endDate],
    ),
    initialData: EMPTY_SLOTS,
  });
  const { run } = slotsTask;
  const reloadSlots = useCallback(() => {
    void run().catch((error) => console.error("useAffiliateBookingOptions", error));
  }, [run]);

  useEffect(() => {
    if (providerToken) reloadSlots();
  }, [providerToken, reloadSlots, refreshKey]);

  const available = useAvailableTeleconsultoria();
  const slots = slotsTask.data ?? EMPTY_SLOTS;
  const choices = useMemo(
    () => buildTeleconsultorChoices(slots, available.rows),
    [slots, available.rows],
  );
  const presence = useTeleconsultoriaPresence(
    choices.filter((choice) => choice.isAvailableNow).map((choice) => choice.teleconsultor.reference),
  );

  const { reload: reloadAvailable } = available;
  const reload = useCallback(() => {
    reloadSlots();
    reloadAvailable();
  }, [reloadSlots, reloadAvailable]);

  return {
    choices,
    presence,
    isLoading: slotsTask.loading || available.loading,
    hasError: slotsTask.status === "error",
    reload,
  };
}
